import React, { useState, useEffect, useCallback } from 'react';
import ArticleCard from './ArticleCard';

/**
 * FeaturedArticlesCarousel component
 * Displays multiple featured articles in an auto-playing carousel
 * Features: navigation arrows, indicator dots, auto-play, keyboard navigation, slide counter
 * Requirements: Responsive design with TailwindCSS
 * 
 * @param {Object} props - Component props
 * @param {Array} props.articles - Array of featured articles to display
 */
const FeaturedArticlesCarousel = ({ articles = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Navigate to next slide
  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === articles.length - 1 ? 0 : prevIndex + 1
    );
  }, [articles.length]);

  // Navigate to previous slide
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? articles.length - 1 : prevIndex - 1
    );
  }, [articles.length]);

  // Navigate to specific slide
  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (isPaused || articles.length <= 1) return;

    const interval = setInterval(() => {
      goToNext();
    }, 5000); // Auto-play every 5 seconds

    return () => clearInterval(interval);
  }, [isPaused, goToNext, articles.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  // Handle empty articles array
  if (!articles || articles.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to VASTP News</h2>
        <p className="text-gray-600">Featured content will appear here</p>
      </div>
    );
  }

  // Single article - no carousel needed
  if (articles.length === 1) {
    return <ArticleCard article={articles[0]} variant="featured" />;
  }

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="region"
      aria-label="Featured articles carousel"
      aria-live="polite"
    >
      {/* Carousel slides */}
      <div className="relative overflow-hidden rounded-lg">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {articles.map((article, index) => (
            <div 
              key={article.id || index} 
              className="min-w-full"
              aria-hidden={index !== currentIndex}
            >
              <ArticleCard article={article} variant="featured" />
            </div>
          ))}
        </div>
      </div>

      {/* Previous button */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label="Previous slide"
      >
        <svg 
          className="w-6 h-6" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M15 19l-7-7 7-7" 
          />
        </svg>
      </button>

      {/* Next button */}
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label="Next slide"
      >
        <svg 
          className="w-6 h-6" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9 5l7 7-7 7" 
          />
        </svg>
      </button>

      {/* Indicator dots and slide counter */}
      <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
        {/* Dots */}
        <div className="flex gap-2">
          {articles.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-white w-8' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentIndex}
            />
          ))}
        </div>

        {/* Slide counter */}
        <div className="bg-black/50 text-white text-sm px-3 py-1 rounded-full">
          {currentIndex + 1} / {articles.length}
        </div>
      </div>

      {/* Pause indicator */}
      {isPaused && (
        <div className="absolute top-4 right-4 bg-black/50 text-white text-xs px-2 py-1 rounded">
          Paused
        </div>
      )}
    </div>
  );
};

export default FeaturedArticlesCarousel;
